<script lang="ts">
  /// <reference types="google.maps" />
  import { onMount, onDestroy, mount, unmount, tick } from 'svelte';
  import {
    MarkerClusterer,
    SuperClusterAlgorithm,
    type Cluster,
  } from '@googlemaps/markerclusterer';
  import { loadGoogleMaps } from './maps-loader';
  import Icon from './Icon.svelte';
  import MapPopup from './MapPopup.svelte';
  import MapDiscoveryPopup from './MapDiscoveryPopup.svelte';
  import { requestLocation } from './location-service.svelte';
  import { BRAND_PRIMARY_HEX, ME_MARKER_HEX } from '~/shared/api.js';
  import type {
    PoiView,
    TextSearchResult,
    TripCityView,
    ViewportRect,
  } from '~/shared/api.js';

  let {
    pois,
    pinTint = BRAND_PRIMARY_HEX,
    activeCity,
    routeEmpty = true,
    routePoiIds = new Set<number>(),
    discoveryActive = false,
    discoveryResults = [] as TextSearchResult[],
    discoveryHint = null,
    focusedPlaceId = null,
    routeStops = null,
    mapLanguage,
    hoursLocale,
    onPoiOpen,
    onPoiSelect,
    onMapPan,
    onMapUserMove,
    onAddToRoute,
    onDiscoveryPick,
    onDiscoveryBookmark,
  }: {
    pois: PoiView[];
    /** Hex color for pins/clusters. Caller picks (e.g. group tint when one
     *  group is filtered, brand primary otherwise). */
    pinTint?: string;
    activeCity: TripCityView | null;
    /** True when the trip's route has zero stops — passed through to the
     *  marker popup so its "Route" / "Add to route" label is state-aware. */
    routeEmpty?: boolean;
    /** Set of POI ids already in the route — disables the popup's
     *  "Add to route" button when this POI is in it. */
    routePoiIds?: Set<number>;
    /** Discovery mode is on. Bookmark pins dim; discovery results render
     *  as outline pins; clustering is disabled. */
    discoveryActive?: boolean;
    /** Text-search results to render as discovery pins. Only consulted
     *  when discoveryActive. Results whose placeId is already bookmarked
     *  are skipped — those POIs render via the normal bookmark pin path
     *  (dimmed). */
    discoveryResults?: TextSearchResult[];
    /** Optional message overlaid on the map during discovery mode —
     *  e.g. "Type what you're looking for, then tap Search here" or
     *  "Nothing here for 'cafe'". Rendered as a centered card. */
    discoveryHint?: string | null;
    /** placeId of the currently focused POI — the one whose detail
     *  the parent is showing in the drawer/popup/sidebar. We swap the
     *  matching pin's icon to a primary-colored variant so the user
     *  can tell which pill they're looking at. */
    focusedPlaceId?: string | null;
    /** Ordered list of route stops with resolved coordinates. When
     *  non-null, MapView enters "route mode": bookmark + discovery
     *  pins are hidden, numbered markers + a straight-line polyline
     *  render the route instead. */
    routeStops?: Array<{ poiId: number; lat: number; lng: number }> | null;
    /** Locked at script-load time. Passed to Google Maps JS via the
     *  loader's `language=` URL param. Change requires a page reload. */
    mapLanguage?: string;
    /** Language the saved POIs' hours were fetched in — passed
     *  through to MapPopup so the today-line matcher uses the right
     *  locale. */
    hoursLocale?: string | null;
    onPoiOpen: (poi: PoiView) => void;
    /** Fires when a bookmark popup opens (with the POI's placeId) or
     *  closes (with null). Lets the parent sync the desktop sidebar's
     *  scroll/highlight with the currently-open pin. */
    onPoiSelect?: (placeId: string | null) => void;
    /** Fires on user-initiated pan/zoom (post-initial-fit) with the current
     *  visible rectangle. The caller surfaces a "This area" chip so the user
     *  can commit it as the search bias. */
    onMapPan?: (viewport: ViewportRect) => void;
    /** Fires only on user-initiated movement (dragend, zoom_changed) —
     *  NOT on every idle. Used to mark the discovery pill as dirty
     *  without false positives from layout-driven resizes (sidebar
     *  appearing, viewport rotation, etc.) which also fire idle. */
    onMapUserMove?: () => void;
    /** Append this POI to the route + open the Route view. Wired through
     *  to the marker popup. */
    onAddToRoute?: (poi: PoiView) => void;
    /** User tapped a discovery pin (or a row tied to one) — parent uses
     *  this to sync the sidebar/drawer focus highlight. The actual popup
     *  is opened by MapView internally. */
    onDiscoveryPick?: (result: TextSearchResult) => void;
    /** User pressed "Add to trip" inside the discovery popup. Parent
     *  runs the bookmark API + state update and (after that lands)
     *  typically calls focusPoi(newPoiId) to surface the new bookmark. */
    onDiscoveryBookmark?: (result: TextSearchResult) => void;
  } = $props();

  let canvas: HTMLDivElement | null = $state(null);
  let loadError: string | null = $state(null);
  let loading = $state(true);
  let map: google.maps.Map | null = null;
  let infoWindow: google.maps.InfoWindow | null = null;
  let openPoiId: number | null = null;
  let markersByPoiId = new Map<number, google.maps.Marker>();
  let discoveryMarkersByPlaceId = new Map<string, google.maps.Marker>();
  let routeMarkers = new Map<number, google.maps.Marker>();
  let routePolyline: google.maps.Polyline | null = null;
  let clusterer: MarkerClusterer | null = null;
  let didInitialFit = false;
  let lastPinTint: string | null = null;
  let lastDiscoveryActive = false;
  let meMarker: google.maps.Marker | null = null;
  let toast: string | null = $state(null);
  let toastTimer: ReturnType<typeof setTimeout> | null = null;
  // Google's "Silver" preset, copied from
  // https://developers.google.com/maps/documentation/javascript/styling
  const SILVER_STYLE: google.maps.MapTypeStyle[] = [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  ];

  onMount(async () => {
    try {
      const maps = await loadGoogleMaps(mapLanguage);
      if (!canvas) return;
      map = new maps.Map(canvas, {
        center: activeCity ? { lat: activeCity.lat, lng: activeCity.lng } : { lat: 0, lng: 0 },
        zoom: activeCity ? 12 : 2,
        styles: SILVER_STYLE,
        disableDefaultUI: false,
        clickableIcons: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      infoWindow = new maps.InfoWindow({ headerDisabled: true });
      loading = false;
      renderMarkers();
      attachPanListeners();
    } catch (e) {
      loadError = e instanceof Error ? e.message : 'Map failed to load';
      loading = false;
    }
  });

  onDestroy(() => {
    disposePopup();
    clusterer?.clearMarkers();
    clusterer = null;
    clearMarkers();
    for (const marker of discoveryMarkersByPlaceId.values()) {
      google.maps.event.clearInstanceListeners(marker);
      marker.setMap(null);
    }
    discoveryMarkersByPlaceId.clear();
    for (const marker of routeMarkers.values()) marker.setMap(null);
    routeMarkers.clear();
    if (routePolyline) {
      routePolyline.setMap(null);
      routePolyline = null;
    }
    if (toastTimer) clearTimeout(toastTimer);
    meMarker?.setMap(null);
    meMarker = null;
    infoWindow?.close();
    infoWindow = null;
    map = null;
  });

  function attachPanListeners() {
    if (!map) return;
    // 'idle' fires once after pan/zoom animations settle — natural
    // debouncer that subsumes both user pans and any layout resize.
    // We emit bounds on every idle (including the first one from the
    // initial fitBounds) so the parent's mapBounds populates promptly.
    map.addListener('idle', emitBounds);
    // For the *user-intent* signal (drives the discovery pill dirty
    // state), we listen to dragend + zoom_changed specifically — these
    // only fire from actual user input, not layout-driven resizes.
    map.addListener('dragend', () => onMapUserMove?.());
    map.addListener('zoom_changed', () => onMapUserMove?.());
  }

  function emitBounds() {
    if (!map || !onMapPan) return;
    const b = map.getBounds();
    if (!b) return;
    const ne = b.getNorthEast();
    const sw = b.getSouthWest();
    onMapPan({
      north: ne.lat(),
      south: sw.lat(),
      east: ne.lng(),
      west: sw.lng(),
    });
  }

  function showToast(message: string, ms = 5000) {
    toast = message;
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast = null;
      toastTimer = null;
    }, ms);
  }

  async function onLocateMe() {
    const result = await requestLocation({ feature: 'Find me on the map' });
    if (result.ok) {
      dropMeMarker({ lat: result.lat, lng: result.lng });
      return;
    }
    if (result.reason === 'cancelled') return;
    showToast("Location is needed for 'Locate me'. Enable it in your browser settings.");
  }

  function meIcon(): google.maps.Icon {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
      <circle cx="20" cy="20" r="20" fill="${ME_MARKER_HEX}" fill-opacity="0.18"/>
      <circle cx="20" cy="20" r="8" fill="${ME_MARKER_HEX}" stroke="#ffffff" stroke-width="3"/>
    </svg>`;
    return {
      url: 'data:image/svg+xml;utf8,' + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(40, 40),
      anchor: new google.maps.Point(20, 20),
    };
  }

  function dropMeMarker(position: { lat: number; lng: number }) {
    if (!map) return;
    if (meMarker) {
      meMarker.setPosition(position);
    } else {
      meMarker = new google.maps.Marker({
        position,
        map,
        title: 'Your location',
        icon: meIcon(),
        zIndex: 999,
      });
    }
    map.panTo(position);
    if ((map.getZoom() ?? 0) < 13) map.setZoom(15);
  }

  function clearMarkers() {
    if (clusterer) clusterer.clearMarkers();
    for (const m of markersByPoiId.values()) {
      // Google Maps' event system retains internal references until
      // clearInstanceListeners runs; setMap(null) alone leaks the click
      // listener captured per marker.
      google.maps.event.clearInstanceListeners(m);
      m.setMap(null);
    }
    markersByPoiId.clear();
  }

  function isMobile(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < 640;
  }

  function clusterIcon(count: number): string {
    // Inline SVG → data URL. Color matches the active pin fill so the
    // cluster bubble reads as "the same set of POIs, grouped." Size
    // scales lightly with count so dense areas read as larger.
    const scale = count < 10 ? 1 : count < 50 ? 1.15 : count < 200 ? 1.3 : 1.45;
    const r = Math.round(20 * scale);
    const w = r * 2;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${w}" viewBox="0 0 ${w} ${w}">
      <circle cx="${r}" cy="${r}" r="${r - 3}" fill="${pinTint}" fill-opacity="0.25"/>
      <circle cx="${r}" cy="${r}" r="${r - 8}" fill="${pinTint}"/>
    </svg>`;
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
  }

  function clusterRenderer() {
    return {
      render: ({ count, position }: Cluster) => {
        const scale = count < 10 ? 1 : count < 50 ? 1.15 : count < 200 ? 1.3 : 1.45;
        const size = Math.round(40 * scale);
        return new google.maps.Marker({
          position,
          icon: {
            url: clusterIcon(count),
            scaledSize: new google.maps.Size(size, size),
            anchor: new google.maps.Point(size / 2, size / 2),
          },
          label: {
            text: String(count),
            color: '#ffffff',
            fontSize: count < 100 ? '13px' : '12px',
            fontWeight: '600',
          },
          // Above individual pins; below the user-location halo.
          zIndex: 100 + count,
        });
      },
    };
  }

  // Teardrop centered in a 32×42 viewBox: rounded head over the upper
  // ~24px, tapering to a tip at (16, 42). Origin shifted from the old
  // centered-at-(0,0) Symbol path so the SVG fits a top-left viewBox.
  const POI_PIN_PATH =
    'M16 0 C 24.84 0 32 7.16 32 16 C 32 25 24 32 16 42 C 8 32 0 25 0 16 C 0 7.16 7.16 0 16 0 Z';

  // Icons are keyed by (category, dim, pinTint) — a tint change (filter
  // toggle) or a discovery-toggle invalidates the dim/tint dimension but
  // not the glyph, so most pins recompute their key but hit a sibling
  // entry already in cache after the first re-render.
  const poiPinIconCache = new Map<string, google.maps.Icon>();

  function poiPinIcon(
    category: string | null,
    opts: { dim?: boolean } = {},
  ): google.maps.Icon {
    const dim = opts.dim ?? false;
    const key = `${category ?? ''}|${dim ? 'd' : 'n'}|${pinTint}`;
    const cached = poiPinIconCache.get(key);
    if (cached) return cached;

    const glyph = categoryGlyph(category);
    // Glyph sits inside the round head — center at (16, 14), 14×14 box
    // scaled from the 24×24 Lucide viewBox. Centered on the head's
    // widest point (y≈16) rather than its geometric midpoint so the
    // glyph reads as sitting *in* the bulge, not floating at the top.
    // Stroke is widened in source coords so it remains visible after
    // scaling (2.5 × 14/24 ≈ 1.46px).
    const glyphPx = 14;
    const glyphScale = glyphPx / 24;
    const glyphX = 16 - glyphPx / 2;
    const glyphY = 14 - glyphPx / 2;
    const glyphSvg = glyph
      ? `<g transform="translate(${glyphX} ${glyphY}) scale(${glyphScale})" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">${glyph}</g>`
      : '';
    const opacity = dim ? 0.4 : 1;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
      <g opacity="${opacity}">
        <path d="${POI_PIN_PATH}" fill="${pinTint}" stroke="#ffffff" stroke-width="2"/>
        ${glyphSvg}
      </g>
    </svg>`.replace(/\s+/g, ' ').trim();

    const icon: google.maps.Icon = {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(32, 42),
      anchor: new google.maps.Point(16, 42),
    };
    poiPinIconCache.set(key, icon);
    return icon;
  }

  /** Map a Google Places category to inline SVG path data for a small
   *  monochrome glyph (Lucide-derived, 24×24 viewBox). Returned string
   *  is one or more <path>/<polygon> elements ready to embed inside an
   *  outer <g>. Empty for unrecognized categories. */
  function categoryGlyph(category: string | null): string {
    if (!category) return '';
    const c = category.toLowerCase();
    // Specific patterns matched first: `convenience_store` and `drugstore`
    // both contain `store` and would otherwise fall into the shopping bag
    // branch below.
    if (
      c.includes('convenience_store') ||
      c.includes('supermarket') ||
      c.includes('grocery')
    ) {
      // Shopping cart
      return '<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>';
    }
    if (c.includes('pharmacy') || c.includes('drugstore')) {
      // Pill
      return '<path d="M10.5 20.5l10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7z"/><path d="M8.5 8.5l7 7"/>';
    }
    if (
      c.includes('train_station') ||
      c.includes('subway_station') ||
      c.includes('light_rail') ||
      c.includes('transit_station')
    ) {
      // Train — body with window seam and splayed wheels
      return '<rect x="5" y="3" width="14" height="16" rx="2"/><path d="M5 11h14"/><path d="M8 19l-2 3"/><path d="M16 19l2 3"/>';
    }
    if (c.includes('bus_station')) {
      // Bus
      return '<rect x="3" y="6" width="18" height="12" rx="2"/><path d="M3 12h18"/><circle cx="7" cy="18" r="1.5"/><circle cx="17" cy="18" r="1.5"/>';
    }
    if (c.includes('airport')) {
      // Plane
      return '<path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.2.6-.6.5-1.1z"/>';
    }
    if (c.includes('atm') || c.includes('bank')) {
      // Credit card
      return '<rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/>';
    }
    if (c.includes('tourist_attraction')) {
      // Camera
      return '<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/>';
    }
    if (c.includes('gas_station')) {
      // Fuel pump
      return '<path d="M3 22h12"/><path d="M4 9h10"/><path d="M14 22V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18"/><path d="M14 13h2a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2v-9l-3-3"/>';
    }
    if (c.includes('hospital') || c.includes('medical') || c.includes('clinic')) {
      // Cross in circle
      return '<circle cx="12" cy="12" r="10"/><path d="M12 7v10"/><path d="M7 12h10"/>';
    }
    if (
      c.includes('church') ||
      c.includes('mosque') ||
      c.includes('synagogue') ||
      c.includes('hindu_temple') ||
      c.includes('buddhist_temple') ||
      c.includes('shinto_shrine') ||
      c.includes('place_of_worship')
    ) {
      // Religious building — peaked roof + small cross/spire ornament.
      // Generic enough to read for churches, temples, and shrines.
      return '<path d="M12 2v4"/><path d="M10 4h4"/><path d="M4 11l8-5 8 5v10H4z"/><rect x="10" y="14" width="4" height="7"/>';
    }
    if (c.includes('bakery') || c.includes('bread') || c.includes('cake')) {
      // Cake slice — cherry on top, angled cut surface, two layer lines
      return '<circle cx="9" cy="7" r="2"/><path d="M7.2 7.9 3 11v9c0 .6.4 1 1 1h16c.6 0 1-.4 1-1v-9c0-2-3-6-7-8l-3.6 2.6"/><path d="M16 13H3"/><path d="M16 17H3"/>';
    }
    if (c.includes('cafe') || c.includes('coffee')) {
      // Coffee cup
      return '<path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1z"/><path d="M16 9h1a4 4 0 0 1 0 8h-1"/>';
    }
    if (c.includes('restaurant') || c.includes('food') || c.includes('meal')) {
      // Utensils — fork left, knife right
      return '<path d="M3 2v7c0 1 1 2 2 2h4c1 0 2-1 2-2V2"/><path d="M7 11v11"/><path d="M21 15V2a5 5 0 0 0-5 5v8h5z"/><path d="M21 15v7"/>';
    }
    if (c.includes('bar') || c.includes('night_club')) {
      // Wine glass
      return '<path d="M8 22h8"/><path d="M12 14v8"/><path d="M12 15a5 5 0 0 0 5-5c0-2-.5-4-2-8H9c-1.5 4-2 6-2 8a5 5 0 0 0 5 5z"/>';
    }
    if (c.includes('lodging') || c.includes('hotel')) {
      // Bed
      return '<path d="M2 4v16"/><path d="M2 8h18a2 2 0 0 1 2 2v10"/><path d="M2 17h20"/><path d="M6 8v9"/>';
    }
    if (c.includes('shopping') || c.includes('store') || c.includes('mall')) {
      // Shopping bag
      return '<path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>';
    }
    if (c.includes('park') || c.includes('zoo') || c.includes('amusement')) {
      // Tree — simplified
      return '<path d="M12 2L7 9h3l-3 5h10l-3-5h3z"/><path d="M11 14v6h2v-6z"/>';
    }
    if (c.includes('museum') || c.includes('gallery') || c.includes('landmark')) {
      // Landmark — columns + roof
      return '<path d="M3 22h18"/><path d="M6 18v-7"/><path d="M10 18v-7"/><path d="M14 18v-7"/><path d="M18 18v-7"/><polygon points="12 2 20 7 4 7"/>';
    }
    return '';
  }

  function discoveryPinIcon(
    result: TextSearchResult,
    opts: { active?: boolean } = {},
  ): google.maps.Icon {
    // Per-result SVG pill marker. Combines a monochrome category glyph
    // (cafe → cup, restaurant → fork, etc.) with the ★ rating on the
    // dark ink fill. Generated as an inline SVG data URI per result;
    // cheap (< 1KB each) and keeps us on the legacy Marker API
    // (AdvancedMarkerElement would require mapId + library migration).
    //
    // `active` swaps the fill from ink to the brand primary color so
    // the user can tell which pin matches the POI surfaced in the
    // drawer/popup/sidebar.
    const glyph = categoryGlyph(result.category);
    const hasRating = result.rating !== null;
    const ratingText = hasRating ? result.rating!.toFixed(1) : '';
    const fillColor = opts.active ? '#ff385c' : '#1a1a1a';
    // Width tuned for each combination. Tight on padding to keep the
    // marker visually compact at typical zoom levels.
    let w: number;
    if (glyph && hasRating) w = 76;
    else if (hasRating) w = 64;
    else if (glyph) w = 32;
    else w = 28;
    const h = 32;

    // Glyph rendering — Lucide paths come in a 24×24 viewBox; we scale
    // to 14px inside the pill. Stroke is widened in source coords so
    // it remains visible after scaling (2.5 × (14/24) ≈ 1.46px).
    const glyphPx = 14;
    const glyphScale = glyphPx / 24;
    const glyphX = glyph && !hasRating ? (w - glyphPx) / 2 : 10;
    const glyphY = (h - glyphPx) / 2;
    const glyphSvg = glyph
      ? `<g transform="translate(${glyphX} ${glyphY}) scale(${glyphScale})" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">${glyph}</g>`
      : '';

    // Rating text — left-aligned next to the glyph, or centered if
    // there's no glyph.
    let bodySvg = '';
    if (hasRating) {
      const tx = glyph ? glyphX + glyphPx + 4 : w / 2;
      const anchor = glyph ? 'start' : 'middle';
      bodySvg = `<text x="${tx}" y="${h / 2 + 4.5}" font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" font-size="13" font-weight="600" text-anchor="${anchor}" fill="#ffffff"><tspan fill="#fbbf24">★</tspan><tspan fill="#ffffff"> ${ratingText}</tspan></text>`;
    } else if (!glyph) {
      bodySvg = `<circle cx="${w / 2}" cy="${h / 2}" r="3.5" fill="#ffffff"/>`;
    }

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
      <defs>
        <filter id="s" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-color="rgba(0,0,0,0.35)"/>
        </filter>
      </defs>
      <rect x="2" y="2" width="${w - 4}" height="${h - 4}" rx="14" ry="14"
        fill="${fillColor}" stroke="#ffffff" stroke-width="2" filter="url(#s)"/>
      ${glyphSvg}
      ${bodySvg}
    </svg>`.replace(/\s+/g, ' ').trim();
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(w, h),
      // Anchor at bottom-center: the pill sits *above* the actual
      // location, like a label pointing down at it.
      anchor: new google.maps.Point(w / 2, h - 2),
    };
  }

  // Numbered route-stop icons are cached because reordering a route
  // changes every marker's number — without the cache we'd rebuild
  // and re-encode the same SVG strings on every re-render.
  const routeStopIconCache = new Map<number, google.maps.Icon>();
  function routeStopIcon(n: number): google.maps.Icon {
    const cached = routeStopIconCache.get(n);
    if (cached) return cached;
    const w = 32;
    const h = 32;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
      <defs>
        <filter id="rs" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-color="rgba(0,0,0,0.35)"/>
        </filter>
      </defs>
      <circle cx="${w / 2}" cy="${h / 2}" r="13" fill="#1a1a1a" stroke="#ffffff" stroke-width="2" filter="url(#rs)"/>
      <text x="${w / 2}" y="${h / 2 + 5}" text-anchor="middle" font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" font-size="14" font-weight="600" fill="#ffffff">${n}</text>
    </svg>`.replace(/\s+/g, ' ').trim();
    const icon: google.maps.Icon = {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(w, h),
      anchor: new google.maps.Point(w / 2, h / 2),
    };
    routeStopIconCache.set(n, icon);
    return icon;
  }

  // Track the set of route stop POI ids so we only re-fit the camera
  // when stops are added/removed — pure reordering keeps the same
  // bounds and a re-fit there is jumpy.
  let lastRouteStopIds = '';
  function renderRouteMode() {
    if (!map) return;
    // Clear bookmark + discovery markers — route mode owns the map.
    if (clusterer) {
      clusterer.setMap(null);
      clusterer = null;
    }
    for (const [id, m] of markersByPoiId) {
      google.maps.event.clearInstanceListeners(m);
      m.setMap(null);
      markersByPoiId.delete(id);
    }
    for (const m of discoveryMarkersByPlaceId.values()) {
      google.maps.event.clearInstanceListeners(m);
      m.setMap(null);
    }
    discoveryMarkersByPlaceId.clear();

    const stops = routeStops ?? [];
    const wanted = new Set(stops.map((s) => s.poiId));
    for (const [id, m] of [...routeMarkers]) {
      if (!wanted.has(id)) {
        m.setMap(null);
        routeMarkers.delete(id);
      }
    }
    stops.forEach((stop, idx) => {
      const icon = routeStopIcon(idx + 1);
      const existing = routeMarkers.get(stop.poiId);
      if (existing) {
        existing.setIcon(icon);
        existing.setPosition({ lat: stop.lat, lng: stop.lng });
        existing.setZIndex(50 + idx);
        return;
      }
      const m = new google.maps.Marker({
        map,
        position: { lat: stop.lat, lng: stop.lng },
        icon,
        zIndex: 50 + idx,
      });
      routeMarkers.set(stop.poiId, m);
    });

    // Polyline through stops — straight lines, no Directions API.
    if (routePolyline) {
      routePolyline.setMap(null);
      routePolyline = null;
    }
    if (stops.length >= 2) {
      routePolyline = new google.maps.Polyline({
        map,
        path: stops.map((s) => ({ lat: s.lat, lng: s.lng })),
        strokeColor: '#1a1a1a',
        strokeOpacity: 0.55,
        strokeWeight: 3,
        geodesic: false,
      });
    }

    // Re-fit only when the SET of stops changes (sorted ids), not on
    // reorder. Re-ordering keeps the same bounds — refitting then
    // just yanks the camera around for no visible benefit.
    const stopIds = stops
      .map((s) => s.poiId)
      .slice()
      .sort((a, b) => a - b)
      .join(',');
    if (stops.length > 0 && stopIds !== lastRouteStopIds) {
      const bounds = new google.maps.LatLngBounds();
      for (const s of stops) bounds.extend({ lat: s.lat, lng: s.lng });
      fitToPoisInternal(bounds);
    }
    lastRouteStopIds = stopIds;
  }

  function renderMarkers() {
    if (!map || !infoWindow) return;
    if (routeStops !== null) {
      renderRouteMode();
      return;
    }

    // Diff against existing markers: keep what's still present, remove what's
    // gone, add what's new. A pinTint change (filter toggle) re-applies the
    // icon to every kept marker. Avoids tearing down the clusterer on every
    // filter change, which both stutters and resets cluster animation state.
    const newIds = new Set(pois.map((p) => p.id));
    const tintChanged = lastPinTint !== null && lastPinTint !== pinTint;
    const discoveryToggled = lastDiscoveryActive !== discoveryActive;
    lastPinTint = pinTint;
    lastDiscoveryActive = discoveryActive;

    const removed: google.maps.Marker[] = [];
    for (const [id, marker] of [...markersByPoiId]) {
      if (!newIds.has(id)) {
        google.maps.event.clearInstanceListeners(marker);
        marker.setMap(null);
        markersByPoiId.delete(id);
        removed.push(marker);
      }
    }

    const added: google.maps.Marker[] = [];
    const bounds = new google.maps.LatLngBounds();
    for (const poi of pois) {
      bounds.extend({ lat: poi.lat, lng: poi.lng });
      const existing = markersByPoiId.get(poi.id);
      if (existing) {
        if (tintChanged || discoveryToggled) {
          existing.setIcon(poiPinIcon(poi.category, { dim: discoveryActive }));
        }
        continue;
      }
      const marker = new google.maps.Marker({
        position: { lat: poi.lat, lng: poi.lng },
        title: poi.customName ?? poi.name,
        icon: poiPinIcon(poi.category, { dim: discoveryActive }),
      });
      marker.addListener('click', () => {
        if (openPoiId === poi.id) closePopup();
        else openPopup(poi, marker);
      });
      markersByPoiId.set(poi.id, marker);
      added.push(marker);
    }

    // Clustering ↔ discovery: clusters confuse the discovery picture.
    // When discovery toggles on, tear the clusterer down and attach the
    // (dimmed) bookmark markers directly to the map. When it toggles
    // off, rebuild the clusterer from the current set.
    if (discoveryActive) {
      if (clusterer) {
        clusterer.setMap(null);
        clusterer = null;
      }
      // Attach kept + new markers directly. Removed markers were already
      // setMap(null)'d in the loop above.
      for (const marker of markersByPoiId.values()) {
        if (marker.getMap() !== map) marker.setMap(map);
      }
    } else if (!clusterer) {
      // Cluster threshold: tighter on mobile (radius=80) than desktop (120)
      // so dense neighborhoods cluster earlier on small screens.
      clusterer = new MarkerClusterer({
        map,
        markers: Array.from(markersByPoiId.values()),
        algorithm: new SuperClusterAlgorithm({
          radius: isMobile() ? 80 : 120,
          minPoints: 2,
          maxZoom: 18,
        }),
        renderer: clusterRenderer(),
      });
    } else {
      if (removed.length > 0) clusterer.removeMarkers(removed);
      if (added.length > 0) clusterer.addMarkers(added);
      if (tintChanged && removed.length === 0 && added.length === 0) {
        // Force the clusterer to re-render so cluster bubbles pick up the
        // new tint (cluster icons are computed by the renderer using pinTint).
        clusterer.render();
      }
    }

    renderDiscoveryMarkers();

    // Only fit bounds on the first render so a filter toggle doesn't
    // re-frame the camera away from where the user is looking. Filter
    // changes call fitToPois() explicitly (see below).
    if (!didInitialFit && pois.length > 0) {
      fitToPoisInternal(bounds);
      didInitialFit = true;
    }
  }

  function renderDiscoveryMarkers() {
    if (!map) return;
    // Off-mode (or no results): tear all discovery markers down.
    if (!discoveryActive) {
      for (const marker of discoveryMarkersByPlaceId.values()) {
        google.maps.event.clearInstanceListeners(marker);
        marker.setMap(null);
      }
      discoveryMarkersByPlaceId.clear();
      return;
    }
    // alreadyBookmarked results render via the normal bookmark-pin path
    // (which is dimmed in discovery mode), not as discovery pins. That
    // way the user never sees two pins for the same place. We also dedup
    // against the *live* pois set so that bookmarking a discovery result
    // immediately drops its discovery pin even though the API response
    // still has alreadyBookmarked=false in our cached array.
    const bookmarkedPlaceIds = new Set(pois.map((p) => p.placeId));
    const wantedIds = new Set(
      discoveryResults
        .filter((r) => !r.alreadyBookmarked && !bookmarkedPlaceIds.has(r.placeId))
        .map((r) => r.placeId),
    );
    for (const [placeId, marker] of [...discoveryMarkersByPlaceId]) {
      if (!wantedIds.has(placeId)) {
        google.maps.event.clearInstanceListeners(marker);
        marker.setMap(null);
        discoveryMarkersByPlaceId.delete(placeId);
      }
    }
    for (const result of discoveryResults) {
      if (result.alreadyBookmarked) continue;
      if (bookmarkedPlaceIds.has(result.placeId)) continue;
      if (discoveryMarkersByPlaceId.has(result.placeId)) continue;
      const marker = new google.maps.Marker({
        map,
        position: { lat: result.lat, lng: result.lng },
        title: result.name,
        icon: discoveryPinIcon(result),
        zIndex: 50, // Above clustered bookmarks (z=100+) is wrong; keep below.
      });
      marker.addListener('click', () => {
        // Parent decides what to surface — on desktop it calls back into
        // openDiscoveryPopupForResult; on mobile it flips the drawer to
        // detail mode and suppresses the popup entirely.
        onDiscoveryPick?.(result);
      });
      discoveryMarkersByPlaceId.set(result.placeId, marker);
    }
  }

  // Swap the icon on the focused discovery marker to the active variant
  // (primary color) so the user can tell which pin matches what's in
  // the drawer/popup/sidebar. Reverts the previously-active marker when
  // focus shifts. O(1) per focus change — no full marker re-render.
  let lastActivePlaceId: string | null = null;
  $effect(() => {
    const focusId = focusedPlaceId;
    // Revert previously-active marker (if any) to its inactive icon.
    if (lastActivePlaceId !== null && lastActivePlaceId !== focusId) {
      const prev = discoveryMarkersByPlaceId.get(lastActivePlaceId);
      const prevResult = discoveryResults.find((r) => r.placeId === lastActivePlaceId);
      if (prev && prevResult) {
        prev.setIcon(discoveryPinIcon(prevResult, { active: false }));
        prev.setZIndex(50);
      }
      lastActivePlaceId = null;
    }
    if (focusId === null) return;
    const marker = discoveryMarkersByPlaceId.get(focusId);
    const result = discoveryResults.find((r) => r.placeId === focusId);
    if (!marker || !result) return;
    marker.setIcon(discoveryPinIcon(result, { active: true }));
    // Bump z so the active pin stays on top of any overlapping ones.
    marker.setZIndex(120);
    lastActivePlaceId = focusId;
  });

  function fitToPoisInternal(bounds: google.maps.LatLngBounds) {
    if (!map) return;
    if (bounds.isEmpty()) return;
    if (pois.length === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(15);
    } else {
      map.fitBounds(bounds, 64);
    }
  }

  /**
   * Live map bounds at the moment of call. Used by discovery mode's
   * "Search this area" — we want the rect right now, not whatever was
   * last emitted on idle (which can lag a fast pan or miss the first
   * idle if the listener attached after the initial fitBounds).
   */
  export function currentBounds(): ViewportRect | null {
    if (!map) return null;
    const b = map.getBounds();
    if (!b) return null;
    const ne = b.getNorthEast();
    const sw = b.getSouthWest();
    return {
      north: ne.lat(),
      south: sw.lat(),
      east: ne.lng(),
      west: sw.lng(),
    };
  }

  /**
   * Re-frame the camera to fit the current pois. Called by the parent on
   * user-initiated filter changes (group chip / city chip / show-on-map),
   * NOT on data changes like a new bookmark — those would yank the user
   * away from where they were looking.
   */
  export function fitToPois() {
    if (!map) return;
    const bounds = new google.maps.LatLngBounds();
    for (const p of pois) bounds.extend({ lat: p.lat, lng: p.lng });
    fitToPoisInternal(bounds);
  }

  // Mounted Svelte popup component. Replaces the previous innerHTML +
  // domready listener-attach approach. Native DOM nodes (no innerHTML
  // injection) render reliably across browsers — including the mobile-
  // Safari pseudo-element/button quirks that swallowed the close X.
  let popupHost: HTMLDivElement | null = null;
  let popupComponent: ReturnType<typeof mount> | null = null;

  function disposePopup() {
    if (popupComponent) {
      unmount(popupComponent);
      popupComponent = null;
    }
    popupHost = null;
  }

  function closePopup() {
    infoWindow?.close();
    openPoiId = null;
    disposePopup();
    onPoiSelect?.(null);
  }

  function openDiscoveryPopup(
    result: TextSearchResult,
    marker: google.maps.Marker,
  ) {
    if (!infoWindow || !map) return;
    disposePopup();
    openPoiId = null;
    popupHost = document.createElement('div');
    popupComponent = mount(MapDiscoveryPopup, {
      target: popupHost,
      props: {
        result,
        onClose: () => {
          infoWindow?.close();
          disposePopup();
        },
        onAddToTrip: () => {
          // Parent runs the bookmark API + state update; we close the
          // popup so the user sees their action complete. The parent
          // typically follows up with focusPoi(newPoiId) once the new
          // POI is in the pois set.
          infoWindow?.close();
          disposePopup();
          onDiscoveryBookmark?.(result);
        },
      },
    });
    infoWindow.setContent(popupHost);
    infoWindow.open({ map, anchor: marker });
  }

  /** Imperatively open the discovery popup for a given result — used by
   *  the parent when a sidebar/drawer row is tapped. Pans the map if
   *  the marker isn't already visible. */
  export function openDiscoveryPopupForResult(result: TextSearchResult) {
    const marker = discoveryMarkersByPlaceId.get(result.placeId);
    if (!marker || !map) return;
    panToMarker(marker);
    openDiscoveryPopup(result, marker);
  }

  /** Imperatively focus a bookmark POI — pan the camera and open its
   *  popup. Used by the parent after a discovery bookmark lands to
   *  surface the freshly saved place.
   *
   *  Polls for the marker because a fresh bookmark triggers a cascade
   *  in the parent: clearQuery → committedQuery=null → discoveryActive
   *  flips → filteredPois recomputes → our render-markers effect
   *  creates the marker. A single `await tick()` in the caller may not
   *  flush all of those before focusPoi runs, so we wait a few extra
   *  ticks here. Without this, the popup silently fails to appear. */
  export async function focusPoi(poiId: number) {
    for (let i = 0; i < 6; i++) {
      if (markersByPoiId.has(poiId)) break;
      await tick();
    }
    const marker = markersByPoiId.get(poiId);
    const poi = pois.find((p) => p.id === poiId);
    if (!marker || !poi || !map) return;
    panToMarker(marker);
    // If the marker is currently absorbed by a cluster
    // (clusterer attaches the cluster to the map, not the individual
    // marker), opening a popup anchored to it would render over an
    // empty patch of map. Zoom in enough to break the cluster so the
    // pin is visible behind its popup.
    if (marker.getMap() === null && clusterer) {
      const targetZoom = 17;
      const currentZoom = map.getZoom() ?? 12;
      if (currentZoom < targetZoom) {
        map.setCenter(marker.getPosition()!);
        map.setZoom(targetZoom);
        // Wait for the clusterer to redistribute markers after the
        // zoom change so our marker is on the map before openPopup.
        await new Promise<void>((resolve) => {
          google.maps.event.addListenerOnce(map!, 'idle', () => resolve());
        });
      }
    }
    openPopup(poi, marker);
  }

  /** Pan the map so the marker is roughly centered. Skips the pan if
   *  the marker is already within the current viewport. */
  function panToMarker(marker: google.maps.Marker) {
    if (!map) return;
    const pos = marker.getPosition();
    if (!pos) return;
    const bounds = map.getBounds();
    if (bounds && bounds.contains(pos)) return;
    map.panTo(pos);
  }

  function openPopup(poi: PoiView, marker: google.maps.Marker) {
    if (!infoWindow || !map) return;
    disposePopup();
    openPoiId = poi.id;
    onPoiSelect?.(poi.placeId);
    popupHost = document.createElement('div');
    popupComponent = mount(MapPopup, {
      target: popupHost,
      props: {
        poi,
        routeEmpty,
        inRoute: routePoiIds.has(poi.id),
        hoursLocale,
        onClose: closePopup,
        onDetails: () => {
          infoWindow?.close();
          openPoiId = null;
          disposePopup();
          onPoiOpen(poi);
        },
        onAddToRoute: onAddToRoute
          ? (p: PoiView) => {
              infoWindow?.close();
              openPoiId = null;
              disposePopup();
              onAddToRoute(p);
            }
          : undefined,
      },
    });
    infoWindow.setContent(popupHost);
    infoWindow.open({ map, anchor: marker });
  }

  // Re-render markers only when the POI set, tint, discovery mode, or
  // discovery results actually change. TripPage allocates a new
  // filteredPois array on every trip-state mutation; we compare by
  // id-set fingerprint so we don't teardown markers on unrelated updates.
  let lastPoiIdsFingerprint: string | null = null;
  let lastFingerprintTint: string | null = null;
  let lastDiscoveryFingerprint: string | null = null;
  let lastRouteFingerprint: string | null = null;
  $effect(() => {
    // Read all reactive deps unconditionally so they're registered as
    // effect deps even on the first run (when `map` is still null
    // because Google Maps async-loads in onMount). If we bailed on
    // `!map` first, Svelte would never see these reads and the effect
    // wouldn't re-run when the parent later swaps the POI set.
    const fingerprint = pois.map((p) => p.id).join(',');
    const tint = pinTint;
    const discoveryFingerprint =
      (discoveryActive ? '1:' : '0:') +
      discoveryResults.map((r) => r.placeId).join(',');
    const routeFingerprint = routeStops
      ? routeStops
          .map((s) => `${s.poiId}@${s.lat.toFixed(5)},${s.lng.toFixed(5)}`)
          .join('|')
      : 'null';
    if (!map) return;
    if (
      fingerprint === lastPoiIdsFingerprint &&
      tint === lastFingerprintTint &&
      discoveryFingerprint === lastDiscoveryFingerprint &&
      routeFingerprint === lastRouteFingerprint
    ) {
      return;
    }
    lastPoiIdsFingerprint = fingerprint;
    lastFingerprintTint = tint;
    lastDiscoveryFingerprint = discoveryFingerprint;
    lastRouteFingerprint = routeFingerprint;
    renderMarkers();
  });

  // Close the marker popup when the user adds the open POI to the
  // route from elsewhere (card menu, detail sheet). Svelte 5 mount()
  // snapshots props at mount time, so the open popup would otherwise
  // keep showing a stale "Add to route" / "In your route" label.
  $effect(() => {
    void routePoiIds;
    if (openPoiId !== null && routePoiIds.has(openPoiId)) {
      infoWindow?.close();
      openPoiId = null;
      disposePopup();
    }
  });
</script>

<div class="map-wrap">
  {#if loading}
    <div class="state">Loading map…</div>
  {:else if loadError}
    <div class="state error">
      <p>{loadError}</p>
      <p class="hint">Check that <code>VITE_GOOGLE_MAPS_JS_API_KEY</code> is set and the key allows this origin.</p>
    </div>
  {/if}
  <div class="canvas" bind:this={canvas}></div>
  {#if !loading && !loadError && pois.length === 0 && !discoveryActive && (routeStops === null || routeStops.length === 0)}
    {#if activeCity}
      <!-- Active city is set: the map initialized centered on it
           (zoom 12). Show a small banner-style nudge instead of an
           opaque overlay so the user can see the map and use the
           search-this-area discovery flow on it. -->
      <div class="overlay-hint" role="status">
        No bookmarks yet. Type above to search this area — e.g., cafes, museums.
      </div>
    {:else}
      <!-- No active city: the map default-centered at the world
           view, which isn't usable. Cover with the full overlay so
           the user isn't confused by a global gray map. -->
      <div class="overlay-empty">No places to show on the map yet.</div>
    {/if}
  {/if}
  {#if !loading && !loadError && discoveryActive && discoveryHint}
    <div class="overlay-hint" role="status">{discoveryHint}</div>
  {/if}
  {#if !loading && !loadError}
    <button
      type="button"
      class="locate-fab"
      aria-label="Locate me"
      title="Show my location"
      onclick={onLocateMe}
    >
      <Icon name="map-pin" size={20} strokeWidth={2} />
    </button>
  {/if}
  {#if toast}
    <div class="toast" role="status">{toast}</div>
  {/if}
</div>

<style>
  .map-wrap {
    position: relative;
    width: 100%;
    height: clamp(420px, 70vh, 720px);
    border-radius: var(--radius-md);
    overflow: hidden;
    background: var(--color-surface-soft);
  }
  .canvas {
    width: 100%;
    height: 100%;
    /* Safari draws the system focus ring on the canvas div itself when
       the OS restores focus on Cmd+Tab return. Mirror the descendant
       rule to the .canvas element directly. */
    outline: none !important;
    -webkit-tap-highlight-color: transparent;
  }
  /* Suppress every flavor of focus indicator on the map's internal
     tabbable elements. Google Maps' inner divs grab focus on drag;
     when the OS restores focus the browser draws a thick outline (or,
     on Safari, sometimes a box-shadow style ring) that has no useful
     semantics — Maps has its own keyboard navigation indicators.
     Safari ignores :focus-visible after window-restore-focus so we
     suppress on both :focus and :focus-visible. */
  .canvas:focus,
  .canvas:focus-visible,
  .canvas :global(:focus),
  .canvas :global(:focus-visible) {
    outline: none !important;
    box-shadow: none !important;
  }
  .state {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 0.5rem;
    color: var(--color-muted);
    font-size: 14px;
    text-align: center;
    padding: 1rem;
    z-index: 1;
  }
  .state.error { color: var(--color-error); }
  .state .hint { font-size: 12px; color: var(--color-muted); }
  /* Empty map: fill the canvas with a centered message instead of a
     small pill at the top. On mobile the pill sat awkwardly above a
     huge blank gray area; the centered overlay reads as a deliberate
     empty state. */
  .overlay-empty {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 1rem 1.25rem;
    background: var(--color-surface-soft);
    color: var(--color-muted);
    font-size: 14px;
    z-index: 2;
  }
  /* Discovery hint: small floating card near the top so it doesn't
     obscure the map or block clicks on bookmark / discovery pins. */
  .overlay-hint {
    position: absolute;
    top: 1rem;
    left: 50%;
    transform: translateX(-50%);
    max-width: calc(100% - 2rem);
    text-align: center;
    padding: 0.5rem 0.875rem;
    background: var(--color-canvas);
    color: var(--color-ink);
    border-radius: var(--radius-sm);
    font-size: 13px;
    line-height: 1.4;
    box-shadow: rgba(0, 0, 0, 0.12) 0 4px 12px;
    z-index: 2;
    pointer-events: none;
  }
  /* InfoWindow content is rendered inside Google's shadow DOM-ish host —
     but Google injects our HTML straight into a div, so unscoped global
     selectors targeting .map-popup are necessary. Keep them in app.css. */

  .locate-fab {
    position: absolute;
    right: 0.875rem;
    top: 50%;
    transform: translateY(-50%);
    width: 44px;
    height: 44px;
    border-radius: 9999px;
    background: var(--color-canvas);
    color: var(--color-ink);
    border: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow:
      rgba(0, 0, 0, 0.04) 0 0 0 1px,
      rgba(0, 0, 0, 0.08) 0 2px 6px,
      rgba(0, 0, 0, 0.12) 0 4px 12px;
    z-index: 3;
  }
  .locate-fab:hover { background: var(--color-surface-soft); }
  .locate-fab:focus-visible {
    outline: 2px solid var(--color-ink);
    outline-offset: 2px;
  }

  .toast {
    position: absolute;
    left: 50%;
    bottom: 0.875rem;
    transform: translateX(-50%);
    max-width: calc(100% - 4.5rem);
    background: rgba(31, 29, 26, 0.92);
    color: var(--color-on-dark);
    padding: 0.625rem 0.875rem;
    border-radius: var(--radius-sm);
    font-size: 13px;
    line-height: 1.4;
    text-align: center;
    box-shadow: rgba(0, 0, 0, 0.2) 0 4px 12px;
    z-index: 3;
  }
</style>
